import { Box, LoadingOverlay, Modal } from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { useFeatureUsage } from '@/components/subscriptions/useFeatureUsage'
import { useCreateModel } from '@/queries/modelQueries'
import { buildModelLink } from '@/router/paths'
import { showToast } from '@/theme/notifications'
import { MLModelType } from '@/types/model'
import { CreateModelForm, FormValues } from '../CreateModelForm/CreateModelForm'

type CreateModelModalProps = {
  appId: string
  modelType: MLModelType
  opened: boolean
  onClose: () => void
}

export const CreateModelModal = ({
  appId,
  modelType,
  opened,
  onClose
}: CreateModelModalProps) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const { refreshFeatureUsage } = useFeatureUsage('user_ml_models')

  const { mutateAsync: createModel, isPending } = useCreateModel()

  const handleCreateModel = async (values: FormValues) => {
    const { name, labeling_type } = values

    try {
      const { data: model } = await createModel({
        application_id: appId,
        name,
        model_type: modelType,
        labeling_type
      })

      onClose()
      void refreshFeatureUsage()
      void navigate(buildModelLink(appId, model.id))
      showToast(intl.formatMessage({ id: 'models.create.success' }), 'success')
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'models.create.error' })
        ),
        'error'
      )
    }
  }

  return (
    <Modal
      size="xl"
      opened={opened}
      title={<FormattedMessage id="models.create" />}
      onClose={onClose}
    >
      <Box pos="relative">
        <LoadingOverlay visible={isPending} />

        <CreateModelForm
          modelType={modelType}
          onCancel={onClose}
          onSubmit={(values) => void handleCreateModel(values)}
        />
      </Box>
    </Modal>
  )
}
